import { createTheme } from "@material-ui/core/styles";
import { transparentize } from "polished";
import CheckedIcon from "../icons/components/checked-icon";
import ChevronDownIcon from "../icons/components/chevron-down-icon";
import UncheckedIcon from "../icons/components/unchecked-icon";
import { Color } from "./color";

export const bonlivaPipelineTheme = createTheme({
  shape: {
    borderRadius: 8
  },
  palette: {
    type: "light",
    primary: {
      main: Color.PrimaryMain,
      dark: Color.PrimaryDark
    },
    secondary: {
      main: Color.CheckGreen
    },
    tertiary: {
      main: Color.PrimaryMain,
      dark: Color.PrimaryDark
    },
    info: {
      main: Color.DetailsInput,
      light: Color.LightGray
    },
    error: {
      main: Color.Error,
      contrastText: Color.White,
      light: Color.ErrorLight
    },
    background: {
      default: Color.White
    },
    text: {
      primary: Color.Body
    },
    success: {
      main: Color.CheckGreen,
      light: Color.GreenLight
    }
  },
  typography: {
    fontFamily: "Calibre, sans-serif",
    subtitle1: {
      fontSize: "1.05rem",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "16px"
    },
    h1: {
      fontSize: "72px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "58px"
    },
    h2: {
      fontSize: "34px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "32px"
    },
    h3: {
      fontSize: "1.775rem",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "24px"
    },
    h4: {
      fontSize: "20px",
      lineHeight: "22px",
      fontWeight: 500
    },
    h5: {
      fontSize: "1.275rem",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "24px"
    },
    h6: {
      fontSize: "1.15rem",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "16px"
    },
    body1: {
      fontSize: "1.15rem",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "21px"
    },
    body2: {
      fontSize: "1.025rem",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "16px"
    }
  },
  overrides: {
    MuiButton: {
      root: {
        // height: "2.5rem",
        textTransform: "none",
        fontSize: "1.05rem"
      },
      text: {
        boxShadow: "none"
      },
      contained: {
        boxShadow: "none"
      },
      containedPrimary: {
        color: Color.White,
        boxShadow: "none",
        "&:hover": {
          backgroundColor: Color.PrimaryDark
        },
        "&:disabled": {
          backgroundColor: Color.PrimaryMain,
          color: Color.White,
          opacity: 0.5
        }
      },
      containedSecondary: {
        color: Color.White,
        boxShadow: "none",
        "&:hover": {
          backgroundColor: Color.CheckGreen
        },
        "&:disabled": {
          backgroundColor: Color.CheckGreen,
          color: Color.White,
          opacity: 0.5
        }
      },
      outlinedSecondary: {
        color: Color.PrimaryMain,
        borderColor: Color.PrimaryMain,
        boxShadow: "none",
        "&:hover": {
          backgroundColor: transparentize(0.75, Color.PrimaryLight),
          borderColor: Color.PrimaryMain
        },
        "&:disabled": {
          backgroundColor: Color.PrimaryMain,
          color: Color.White,
          borderColor: Color.PrimaryMain,
          opacity: 0.5
        }
      }
    },
    MuiInputBase: {
      root: {
        borderRadius: "0.375rem",
        color: Color.Body,
        "&$focused": {
          color: Color.Body
        }
      },
      formControl: {
        borderRadius: "0.375rem",
        height: "2.5rem",
        "&$focused": {
          borderColor: Color.PrimaryMain
        },
        "&$error": {
          borderColor: Color.Error
        }
      },
      input: {
        boxSizing: "border-box",
        "&::placeholder": {
          color: Color.DetailsInput
        }
      },
      multiline: {
        height: "auto",
        color: Color.Body
      }
    },
    MuiOutlinedInput: {
      root: {
        border: `1px solid ${Color.LightGray}`
      },
      notchedOutline: {
        border: "none"
      },
      inputAdornedStart: {
        paddingLeft: "inherit"
      }
    },
    MuiFormLabel: {
      root: {
        color: Color.Body,
        "&$focused": {
          color: Color.Body
        }
      },
      asterisk: {
        color: Color.Error
        // marginLeft: "-4px",
        // "&::after": {
        //   content: `"*"`,
        //   color: Color.PrimaryDark
        // }
      }
    },

    MuiFormHelperText: {
      root: {
        margin: "0 0.5rem",
        "&$error": {
          margin: "0 0.5rem"
        }
      },
      contained: {
        margin: "0 0.5rem",
        "&$error": {
          margin: "0 0.5rem"
        }
      }
    },

    MuiSelect: {
      root: {
        borderRadius: "0.375rem",
        color: Color.Body,
        padding: "0.75rem 1.25rem",
        height: "2.5rem"
      },
      outlined: {
        border: `1px solid ${Color.LightGray}`,
        "&:active": {
          border: `1px solid ${Color.PrimaryMain}`
        },
        "&:hover ": {
          border: `1px solid ${Color.PrimaryDark}`
        }
      },
      select: {
        borderRadius: "0.375rem",
        "&:focus": {
          borderRadius: "0.375rem",
          backgroundColor: "transparent"
        },
        "&:active": {
          border: `1px solid ${Color.PrimaryMain}`
        }
      }
    },

    MuiInputLabel: {
      root: {
        color: Color.DetailsInput
      },
      formControl: {
        position: "relative",
        transform: "translate(10px, 28px) scale(1)",
        "&$focused": {
          color: Color.Body
        }
      },
      shrink: {
        transform: "translate(0px, 3px) scale(0.75)"
      },

      outlined: {
        transform: "translate(10px, 28px) scale(1)",
        "&$shrink": {
          transform: "translate(0px, 0px) scale(0.75)"
        }
      }
    },
    MuiSwitch: {
      root: {
        width: "2.6875rem",
        height: "1.375rem",
        padding: 0,
        borderRadius: "0.6875rem"
      },
      colorPrimary: {
        "&$checked": {
          color: Color.White
        },
        "&$checked + $track": {
          backgroundColor: Color.PrimaryMain,
          opacity: 1
        }
      },
      colorSecondary: {
        "&$checked": {
          color: Color.White
        },
        "&$checked + $track": {
          backgroundColor: Color.PrimaryMain,
          opacity: 1
        }
      },
      track: {
        backgroundColor: Color.Gray,
        opacity: 1
      },
      thumb: {
        height: "1.1rem",
        width: "1.1rem",
        boxShadow: "none"
      },
      switchBase: {
        padding: "0.15rem",
        "&$checked": {
          transform: "translateX(1.25rem)"
        }
      }
    },
    MuiFormControlLabel: {
      label: {
        marginLeft: "8px"
      }
    },
    MuiRadio: {
      root: {
        color: Color.Gray,
        padding: "5px 10px"
      }
    },
    MuiTabs: {
      root: {
        backgroundColor: Color.White,
        boxShadow: `1px 6px 24px 0px ${Color.BoxShadow}`,
        borderRadius: "0.125rem"
      },
      indicator: {
        backgroundColor: Color.PrimaryMain,
        height: "0.1875rem",
        borderRadius: "0.125rem 0.125rem 0rem 0rem"
      }
    },
    MuiTab: {
      root: {
        color: Color.Gray,
        textTransform: "none",
        minWidth: "unset !important",
        fontSize: "1.05rem"
      }
    },
    MuiCard: {
      root: {
        boxShadow: "0px 8px 24px 0px #80808026",
        padding: "17px 14px 14px 16px",
        borderRadius: "2px",
        "&:hover": {
          boxShadow: "0px 4px 14px 0px #80808086"
        }
      }
    },
    MuiCardContent: {
      root: {
        "&:last-child": {
          paddingBottom: "16px"
        }
      }
    },
    MuiChip: {
      root: {
        color: "black",
        borderRadius: "2px",
        border: "0.5px solid",
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "4px"
      },
      sizeSmall: {
        fontSize: "0.85rem",
        fontWeight: 400,
        lineHeight: "1.1rem"
      },
      labelSmall: { padding: "0 6px" },
      outlinedPrimary: {
        borderColor: Color.PrimaryMain,
        backgroundColor: "#ff867b26"
      }
    },
    MuiTooltip: {
      tooltip: {
        padding: "2px 4px",
        backgroundColor: "#363636",
        boxShadow: "1px 6px 24px 0px #ff867b26",
        fontSize: "0.9rem",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "1.15rem"
      }
    },
    MuiTypography: {
      root: {
        textTransform: "none"
      },
      subtitle1: {
        color: Color.DetailsInput
      }
    },
    MuiTableHead: {
      root: {
        backgroundColor: Color.LightBlue
      }
    },
    MuiTableCell: {
      head: {
        fontWeight: "bold"
      }
    },
    MuiAccordion: {
      root: {
        boxShadow: "none",
        "&:before": {
          display: "none"
        }
      }
    },
    MuiAccordionDetails: {
      root: {
        padding: "8px 10px 16px"
      }
    },
    MuiAccordionSummary: {
      root: {
        padding: "0 10px",
        minHeight: "0 !important",
        "&$expanded": {
          minHeight: "0 !important",
          margin: "0 !important"
        }
      },
      content: {
        margin: "0 !important"
      }
    },

    MuiListItem: {
      root: {
        cursor: "pointer"
      }
    },

    MuiSlider: {
      valueLabel: {
        left: "calc(-50%)",
        top: -22,
        "& *": {
          background: "transparent",
          color: "#000"
        },
        "&:first-child": {
          transform: "translateX(0)"
        }
      },
      thumb: {
        height: 24,
        width: 24,
        backgroundColor: Color.PrimaryMain,
        border: "4px solid white",
        boxShadow: "0px 0px 6px 0px #5a5a5a3b",
        marginTop: -10,
        marginLeft: -12,
        "&:focus, &:hover, &$active": {
          boxShadow: "inherit"
        }
      }
    }
  },

  mixins: {
    childrenMarginRight: (v) => ({
      "& > *:not(:last-child)": {
        marginRight: v
      }
    }),

    childrenMarginBottom: (v) => ({
      "& > *:not(:last-child)": {
        marginBottom: v
      }
    }),

    fullAbsolute: {
      position: "absolute",
      right: 0,
      top: 0,
      bottom: 0,
      left: 0
    }
  },

  props: {
    MuiSelect: {
      variant: "outlined",
      IconComponent: ChevronDownIcon
    },
    MuiTextField: {
      variant: "outlined"
    },
    MuiCheckbox: {
      color: "primary",
      checkedIcon: <CheckedIcon />,
      icon: <UncheckedIcon />,
      disableRipple: true
    },
    MuiRadio: {
      color: "primary"
    },
    MuiAccordionSummary: {
      expandIcon: <ChevronDownIcon style={{ width: "1rem", height: "1rem" }} />
    }
  }
});
